<template>
  <div 
    v-if="open"
    :ref="`selection-list-${fieldName}`"
    class="selection-list position-absolute bg-sachlich-white"
    :style="`width: ${width}px`"
  >
    <div class="selection-items fw-bold">
      <div
        v-for="(item, index) in itemList"
        :key="index"
        :ref="`selection-list-${fieldName}-item-${index}`"
        class="selection-item color-sachlich-black py-1 px-2"
        :class="{ 
          highlighted: index === highlightedIndex, 
          unavailable: !!item.unavailable, 
          divider: !!item.divider 
        }"
        @click="clickHandler(item)"
        @mouseenter="highlightedIndex = index; highlightedIndexChanged(index)"
      >
        {{ item.listDisplay }}
        <span style="flex:1" />
        <Price
          v-if="item.price !== undefined"
          :amount="item.price.amount"
          :cents="true"
          :dash="false"
          :prefix="item.price.prefix"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { KEY_CODES } from '../../general/helper';
import { IItemList } from '../interfaces';
import Price from './Price.vue';

export default defineComponent({
  name: 'SelectionList',
  components: { Price },

  props: {
    fieldName: { type: String, default: '' },
    width: { type: Number, default: 150 },
    open: { type: Boolean, default: false },
    itemList: {
      type: Array<IItemList>,
      default: [],
    },
    selectItem: { type: Function, default: () => console.log('selectItem not implemented') }, // eslint-disable-line no-console
    highlightedIndexChanged: { type: Function, default: () => console.log('highlightedIndexChanged not implemented') }, // eslint-disable-line no-console
  },

  data: () => {
    return {
      highlightedIndex: -1,
    }
  },

  watch: {
    open(newValue: boolean, _oldValue: boolean) {
      if (!window.addEventListener) {
        return
      }
      if (newValue) {
        setTimeout(() => {
          window.addEventListener('click', this.closeHandler)
          window.addEventListener('keydown', this.keyHandler)
        }, 50)
      } else {
        window.removeEventListener('click', this.closeHandler)
        window.removeEventListener('keydown', this.keyHandler)
        this.highlightedIndex = -1
      }
    },
    itemList: {
        handler(_newValue: IItemList[], _oldValue: IItemList[]) {
          this.highlightedIndex = -1
        },
        deep: true
    },
  },

  methods: {
    clickHandler(item: IItemList) {
      this.selectItem(item)
    },

    keyHandler(event: KeyboardEvent) {
      if (!this.open) return

      switch (event.code) {
        case KEY_CODES.ARROW_DOWN:
          {
            let nextIndex = this.highlightedIndex + 1
            const maxIndex = this.itemList.length - 1

            while (nextIndex <= maxIndex) {
              if (!this.itemList[nextIndex].divider) {
                break
              }
              ++nextIndex
            }

            if (nextIndex <= maxIndex && !this.itemList[nextIndex].divider) {
              this.highlightedIndex = nextIndex
            }
          }
          break

        case KEY_CODES.ARROW_UP:
          {
            let nextIndex = this.highlightedIndex - 1

            while (nextIndex >= 0) {
              if (!this.itemList[nextIndex].divider) {
                break
              }
              --nextIndex
            }

            if (nextIndex >= 0 && !this.itemList[nextIndex].divider) {
              this.highlightedIndex = nextIndex
            }
          }
          break

        case KEY_CODES.TAB:
        case KEY_CODES.ENTER:
          this.selectItem(this.itemList[this.highlightedIndex])
          break
      }

      if (this.highlightedIndex > -1) {
        const list = this.$refs[`selection-list-${this.fieldName}`] as HTMLElement

        if (list) {
          const item = (this.$refs[
            `selection-list-${this.fieldName}-item-${this.highlightedIndex}`
          ] as HTMLElement[])[0]

          if (!!item) {
            const listRect = list.getClientRects()[0]
            const itemRect = item.getClientRects()[0]

            let scrollAmount = 0
            scrollAmount += Math.max(0, itemRect.bottom - listRect.bottom)
            scrollAmount += Math.min(0, itemRect.top - listRect.top)
            list.scrollTop += scrollAmount
          }
        }
      }

      this.highlightedIndexChanged(this.highlightedIndex)
    },
    closeHandler(event: MouseEvent) {
      if (!this.open) return

      const selectionList = this.$refs[`selection-list-${this.fieldName}`] as HTMLElement

      if (!!selectionList) {
        const rect = selectionList.getClientRects()[0]

        if (
          event.clientX < rect.left ||
          event.clientX > rect.left + rect.width ||
          (event.clientY < rect.top || event.clientY > rect.top + rect.height)
        ) {
          this.selectItem()
        }
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.selection-list {
  min-height: $default-font-size;
  max-height: 300px;
  z-index: 4;
  transform: scale(1);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  border: 2px solid $color-expinet-blue;
  border-top: none;
}

.selection-items {
  font-size: $default-font-size;
  border: 1px solid $color-expinet-gray;
  cursor: pointer;
}

.selection-item {
  display: flex;

  &.highlighted {
    color: $color-aida-white;
    background-color: $color-expinet-blue;
  }

  &.unavailable {
    color: $color-expinet-gray;
    background: none;
    cursor: default;
  }

  &.divider {
    font-weight: normal;
    color: $color-expinet-dark-gray;
    display: table;
    white-space: nowrap;
    border-bottom: 1px solid $color-expinet-dark-gray;
    width: 100%;
    text-align: center;
  }

  &.divider.highlighted {
    background: none;
    cursor: default;
  }
}
</style>
